import { Reveal } from "@/components/motion/Reveal";
import EarlyAccessForm from "@/components/EarlyAccessForm";
import Marquee from "@/components/Marquee";

export default function Story() {
  return (
    <div data-testid="story-page">
      {/* Hero */}
      <section data-testid="story-hero" className="bg-ivory">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-32 md:pt-48 pb-16">
          <p className="eyebrow eyebrow-brass mb-6">About Veylle</p>
          <h1 className="font-display leading-[1.14]" style={{ fontSize: "clamp(64px, 12vw, 180px)", fontWeight: 300 }}>
            A house, <em className="italic">not a shop.</em>
          </h1>
        </div>
      </section>

      {/* Belief */}
      <section data-testid="story-belief" className="bg-ivory">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <Reveal>
              <p className="font-mono-stamp text-pencil">Chapter I</p>
              <p className="eyebrow eyebrow-brass mt-3">The Belief</p>
            </Reveal>
          </div>
          <div className="md:col-span-9 space-y-10">
            <Reveal>
              <p className="font-display text-3xl md:text-5xl leading-[1.12]" style={{ fontWeight: 300 }}>
                We believe the things worn closest to skin, through heat and travel and long days,
                deserve the care usually saved for <em className="italic">fine fragrance.</em> That protection and comfort
                can feel beautiful, not clinical. Always gentle, never heavy. Made to be kept, not replaced.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-[16px] leading-[1.8] text-pencil max-w-2xl">
                Veylle is a house, not a single product. A small, deliberate collection of functional things,
                each refined until it feels like luxury, each carrying one quiet fragrance sensibility,
                so the whole reads as worn from the same hand. We build slowly, and we build to keep.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Founder note */}
      <section data-testid="story-founder" className="bg-cream">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-40 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Reveal>
              <p className="font-mono-stamp text-pencil">Chapter II</p>
              <p className="eyebrow eyebrow-brass mt-3">A Note from the Founder</p>
              <div className="mt-10 border border-ink/10">
                <img
                  src="https://customer-assets.emergentagent.com/job_underbloom-launch/artifacts/blg1n2ah_Color%20Logos.PNG"
                  alt="Robin, founder of Veylle"
                  className="w-full h-[680px] object-cover"
                  loading="lazy"
                />
              </div>
              <p className="mt-6 font-display italic text-3xl">Robin</p>
            </Reveal>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <Reveal>
              <div className="space-y-6 text-[16px] leading-[1.8]">
                <p>I didn't set out to find a gap in a market. I kept finding gaps in my own days.</p>
                <p>
                  The products I used every day, the ones closest to skin, were the ones I never actually wanted to use.
                  What existed worked, technically. But it was harsh, or awkward, or something I would never choose to wear,
                  so I set it down and did without.
                </p>
                <p>
                  Underbloom is the clearest example. I run warm, and there is a particular self-consciousness in feeling
                  your own skin turn damp, aware of it before anyone else could be. Nothing on the shelf answered that in a
                  way that felt considered rather than clinical. So I made the thing I wanted: invisible, silky, quietly scented,
                  something I would reach for without thinking.
                </p>
                <p>
                  That became the whole idea. Nothing here is accidental, and nothing ships until it earns its place.
                  Veylle exists to keep closing those small gaps, the damp skin, the thing you won't wear, the fix that never quite works,
                  until the overlooked, uncomfortable moments of a day feel quietly handled. Made beautifully enough to leave out,
                  never hidden away.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* How we build */}
      <section data-testid="story-how" className="bg-bone">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="grid md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-5">
              <p className="font-mono-stamp text-pencil">Chapter III</p>
              <p className="eyebrow eyebrow-brass mt-3">How We Build</p>
              <h3 className="font-display text-5xl md:text-6xl mt-8 leading-[1.12]" style={{ fontWeight: 300 }}>
                Closing small <em className="italic">gaps.</em>
              </h3>
            </div>
            <div className="md:col-span-7 md:pl-10">
              <p className="text-[16px] leading-[1.8] max-w-xl">
                We close small gaps — the damp skin, the thing you won't wear, the fix that never quite works —
                one at a time, until the overlooked moments of a day feel quietly handled.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 border-t border-ink">
            {[
              { m: "01", t: "Small batches." },
              { m: "02", t: "Refillable, made to keep." },
              { m: "03", t: "Nothing ships until it earns its place." },
            ].map((x, i) => (
              <Reveal key={x.m} delay={i * 0.1}>
                <div className={`p-8 md:p-10 min-h-[240px] border-b md:border-b-0 border-ink ${i < 2 ? "md:border-r" : ""}`}>
                  <p className="font-mono-stamp text-brass">{x.m}</p>
                  <p className="mt-8 font-display text-3xl md:text-4xl leading-tight" style={{ fontWeight: 400 }}>{x.t}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Join section removed — footer has one already */}
    </div>
  );
}
